// BILLING-EXCLUDED-TAB-001 — Excluded from Billing queue: latest reason inline, full
// append-only lifecycle in a "View History" popover, "Include for Billing" with confirmation.
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, RotateCcw, Loader2, Ban, CheckCircle2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { formatDateOnly, formatDateTimeLocal } from '../../../utils/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v) || 0);
const rowAmount = (o) => o.total_billable_amount ?? o.billing_override_price ?? o.billing_unit_price_snapshot ?? 0;
const dash = (v) => (v ? v : '—');

const ExclusionHistory = ({ order }) => {
  const history = Array.isArray(order.billing_exclusion_history) ? order.billing_exclusion_history : [];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-700 hover:text-indigo-900 hover:underline underline-offset-2"
          data-testid={`exclusion-history-btn-${order.id}`}>
          <History className="w-3.5 h-3.5" /> View History
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" data-testid={`exclusion-history-panel-${order.id}`}>
        <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-semibold text-slate-900">Exclusion history · {order.product_id || order.order_number}</div>
        <ol className="max-h-72 overflow-auto divide-y divide-slate-100">
          {history.length === 0 && (
            <li className="px-4 py-3 text-xs text-slate-500" data-testid={`exclusion-history-empty-${order.id}`}>
              History unavailable — this exclusion predates lifecycle tracking.
            </li>
          )}
          {history.map((h, i) => (
            <li key={i} className="px-4 py-2.5 text-xs" data-testid={`exclusion-history-entry-${order.id}-${i}`}>
              <div className={`flex items-center gap-1.5 font-semibold ${h.action === 'included' ? 'text-emerald-700' : 'text-rose-700'}`}>
                {h.action === 'included' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                {h.action === 'included' ? 'Included for Billing' : 'Excluded from Billing'}
              </div>
              <div className="text-slate-500 mt-0.5">{formatDateTimeLocal(h.at)} · {dash(h.by_name)}</div>
              {h.action === 'excluded' && <div className="text-slate-700 mt-0.5">Reason: {dash(h.reason)}</div>}
            </li>
          ))}
        </ol>
      </PopoverContent>
    </Popover>
  );
};

const IncludeConfirmDialog = ({ order, onClose, onIncluded }) => {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/pricing/billing/orders/${order.id}/include`);
      toast.success(res?.data?.message || 'Order included for billing.');
      onIncluded?.(order);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to include order for billing.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="include-for-billing-dialog">
        <DialogHeader>
          <DialogTitle>Include this order for billing?</DialogTitle>
          <DialogDescription>
            This order will be returned to the Unbilled billing queue and will become eligible for invoice generation again, subject to the normal billing eligibility rules.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700">
          <span className="font-mono font-semibold">{order.product_id || order.order_number}</span>
          {order.billing_excluded_reason && <span className="block mt-0.5 text-slate-500">Current reason: {order.billing_excluded_reason}</span>}
        </div>
        {order.billing_exclusion_source === 'client_dnb' && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="include-dnb-warning">
            <span className="font-semibold">Client is Do Not Bill.</span> This order was excluded by the client's DNB setting. Re-including it overrides DNB for this order only and is recorded in the audit history; future orders for this client stay excluded.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="include-cancel-btn">Cancel</Button>
          <Button onClick={confirm} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="include-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
            Include for Billing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TH = ({ children, className = '' }) => (
  <th className={`px-3 py-2 text-[11px] font-semibold text-white uppercase tracking-wide text-left ${className}`}>{children}</th>
);

export const ExcludedOrdersTable = ({ orders, loading, onIncluded }) => {
  const [target, setTarget] = useState(null);
  const cols = 11;
  return (
    <>
      <table className="w-full" data-testid="excluded-orders-table">
        <thead className="sticky top-0 z-10">
          <tr className="bd-thead-navy border-b border-[#1E3A8A]">
            <TH>Product ID</TH><TH>Client</TH><TH>Product</TH><TH>Property Address</TH><TH>Completed</TH>
            <TH className="text-right">Billing Amount</TH><TH>Excluded Date</TH><TH>Source</TH><TH>Excluded By</TH><TH>Reason</TH><TH className="text-center">Actions</TH>
          </tr>
        </thead>
        <tbody>
          {loading ? [0, 1, 2, 3].map((i) => (
            <tr key={`sk-${i}`} className="border-b border-slate-100" data-testid="billing-skeleton-row">
              {Array.from({ length: cols }).map((_, j) => <td key={j} className="px-3 py-3"><div className="bd-skeleton h-3.5" style={{ width: `${50 + ((i * 7 + j * 11) % 40)}%` }} /></td>)}
            </tr>
          )) : orders.length === 0 ? (
            <tr><td colSpan={cols} className="px-6 py-12 text-center text-sm text-slate-500" data-testid="excluded-empty">No orders are currently excluded from billing.</td></tr>
          ) : orders.map((o) => (
            <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`excluded-row-${o.id}`}>
              <td className="px-3 py-2 font-mono text-sm text-slate-800">{dash(o.product_id || o.order_number)}</td>
              <td className="px-3 py-2 text-sm text-slate-700">{dash(o.client_code)}</td>
              <td className="px-3 py-2 text-sm text-slate-700">{dash(o.client_product)}</td>
              <td className="px-3 py-2 text-sm text-slate-700 truncate max-w-[220px]">{dash(o.property_address_display || o.property_address)}</td>
              <td className="px-3 py-2 text-sm text-slate-500 tabular-nums">{o.completed_at ? formatDateOnly(o.completed_at) : '—'}</td>
              <td className="px-3 py-2 text-sm font-semibold text-slate-900 text-right tabular-nums">{money(rowAmount(o))}</td>
              <td className="px-3 py-2 text-sm text-slate-500 tabular-nums" data-testid={`excluded-date-${o.id}`}>{o.billing_excluded_at ? formatDateOnly(o.billing_excluded_at) : '—'}</td>
              <td className="px-3 py-2 text-sm" data-testid={`excluded-source-${o.id}`}>
                {o.billing_exclusion_source === 'client_dnb'
                  ? <span className="inline-flex items-center rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 text-[11px] font-semibold">Client DNB</span>
                  : <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 text-[11px] font-semibold">Manual</span>}
              </td>
              <td className="px-3 py-2 text-sm text-slate-700" data-testid={`excluded-by-${o.id}`}>{dash(o.billing_excluded_by_name)}</td>
              <td className="px-3 py-2 text-sm text-slate-700 max-w-[260px]">
                <div className="truncate" title={o.billing_excluded_reason || 'Reason unavailable'} data-testid={`excluded-reason-${o.id}`}>{o.billing_excluded_reason || <span className="text-slate-400">— unavailable</span>}</div>
                <ExclusionHistory order={o} />
              </td>
              <td className="px-3 py-2 text-center">
                <button type="button" onClick={() => setTarget(o)}
                  className="bd-chip inline-flex items-center gap-1 h-7 px-2.5 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                  data-testid={`include-for-billing-btn-${o.id}`}>
                  <RotateCcw className="w-3.5 h-3.5" /> Include for Billing
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {target && <IncludeConfirmDialog order={target} onClose={() => setTarget(null)} onIncluded={onIncluded} />}
    </>
  );
};

export default ExcludedOrdersTable;
